export type Theme = 'light' | 'dark';

export interface ThemeColors {
  // Background colors
  background: string;
  backgroundSecondary: string;
  backgroundTertiary: string;
  overlayBackground: string;
  noteTimeBackground: string;
  
  // Text colors
  textPrimary: string;
  textSecondary: string;
  textTertiary: string;
  textQuaternary: string;
  textQuinary: string;
  proText: string;
  
  // Border colors
  border: string;
  borderSecondary: string;
  cardBorderStrong: string;
  headerBorder: string;
  abilityIconUsedBorder: string;
  abilityIconUnusedBorder: string;
  timelineMarkerRoundStart: string;
  
  // Interactive elements
  buttonBackground: string;
  buttonText: string;
  buttonHover: string;
  buttonActive: string;
  buttonPrimary: string;
  buttonPrimaryHover: string;
  buttonPrimaryActive: string;
  buttonPrimaryText: string;
  
  // Special elements
  headerBackground: string;
  headerText: string;
  cardBackground: string;
  cardShadow: string;
  cardShadowStrong: string;
  controlsBoxShadow: string;
  
  // Status colors
  error: string;
  success: string;
  warning: string;
  info: string;

  // Slider colors
  sliderTrackBackground: string;
  sliderTrackBorder: string;

  // Ability usage cell colors
  abilityCellCount1: string;
  abilityCellCount2: string;
  abilityCellCount3: string;
  abilityCellUltimate1: string;
  abilityCellUltimate2: string;
  abilityCellUltimate3: string;

  // Abilities timeline gradient
  abilitiesTimelineGradientTop: string;
  abilitiesTimelineGradientMiddle: string;
  abilitiesTimelineGradientBottom: string;

  // Map colors
  mapAbyss: string;
  mapAscent: string;
  mapBind: string;
  mapBreeze: string;
  mapHaven: string;
  mapSplit: string;
  mapPearl: string;
  mapLotus: string;
  mapSunset: string;
  mapIcebox: string;
  mapFracture: string;
  mapCorrode: string;

  // Map RGBA colors
  mapAbyssRgba: string;
  mapAscentRgba: string;
  mapBindRgba: string;
  mapBreezeRgba: string;
  mapHavenRgba: string;
  mapSplitRgba: string;
  mapPearlRgba: string;
  mapLotusRgba: string;
  mapSunsetRgba: string;
  mapIceboxRgba: string;
  mapFractureRgba: string;
  mapCorrodeRgba: string;
}

export const themes: Record<Theme, ThemeColors> = {
  light: {
    background: '#ffffff',
    backgroundSecondary: '#f8f9fa',
    backgroundTertiary: '#f5f5f5',
    overlayBackground: 'rgba(0, 0, 0, 0.5)',
    noteTimeBackground: '#e3f2fd',
    
    textPrimary: '#333333',
    textSecondary: '#666666',
    textTertiary: '#999999',
    textQuaternary: '#cccccc',
    textQuinary: '#cccccc',
    proText: '#d4af37',
    
    border: '#dee2e6',
    borderSecondary: 'rgba(173, 184, 209, 0.5)',
    cardBorderStrong: '#b0b0b0',
    headerBorder: '#a0a0a0',
    abilityIconUsedBorder: 'rgba(0, 81, 255, 0.363)',
    abilityIconUnusedBorder: '#dee2e6',
    timelineMarkerRoundStart: '#c0c0c0',
    
    buttonBackground: '#e6e6e6',
    buttonText: '#2e2e2e',
    buttonHover: '#e2e6ea',
    buttonActive: '#007bff',
    buttonPrimary: '#0d6efd',
    buttonPrimaryHover: '#0b5ed7',
    buttonPrimaryActive: '#0a58ca',
    buttonPrimaryText: '#ffffff',
    
    headerBackground: '#ffffff',
    headerText: '#222222',
    cardBackground: '#ffffff',
    cardShadow: '0 1px 3px rgba(0, 0, 0, 0.5)',
    cardShadowStrong: '0 8px 25px rgba(0, 0, 0, 0.15), 0 4px 10px rgba(0, 0, 0, 0.1)',
    controlsBoxShadow: '0px -5px 20px rgba(0, 0, 0, 0.05)',
    
    error: '#dc3545',
    success: '#198754',
    warning: '#ffc107',
    info: '#0dcaf0',

    sliderTrackBackground: '#e9ecef',
    sliderTrackBorder: '#b6b6b6',

    // Ability usage cell colors - light theme
    abilityCellCount1: '#b2d4fd',
    abilityCellCount2: '#4a90e2',
    abilityCellCount3: '#1a67c2',
    abilityCellUltimate1: '#ff90b6',
    abilityCellUltimate2: '#e2517d',
    abilityCellUltimate3: '#b31a4d',

    abilitiesTimelineGradientTop: '#f8f9fa',
    abilitiesTimelineGradientMiddle: '#ffffff',
    abilitiesTimelineGradientBottom: '#f8f9fa',

    // Map colors - light theme (slightly muted)
    mapAbyss: '#1e3a8a',
    mapAscent: '#15803d',
    mapBind: '#b45309',
    mapBreeze: '#0891b2',
    mapHaven: '#4338ca',
    mapSplit: '#be185d',
    mapPearl: '#0d9488',
    mapLotus: '#7e22ce',
    mapSunset: '#ea580c',
    mapIcebox: '#0369a1',
    mapFracture: '#b91c1c',
    mapCorrode: '#1e3a8a',

    // Map RGBA colors - light theme
    mapAbyssRgba: '30, 58, 138',
    mapAscentRgba: '21, 128, 61',
    mapBindRgba: '180, 83, 9',
    mapBreezeRgba: '8, 145, 178',
    mapHavenRgba: '67, 56, 202',
    mapSplitRgba: '190, 24, 93',
    mapPearlRgba: '13, 148, 136',
    mapLotusRgba: '126, 34, 206',
    mapSunsetRgba: '234, 88, 12',
    mapIceboxRgba: '3, 105, 161',
    mapFractureRgba: '185, 28, 28',
    mapCorrodeRgba: '30, 58, 138',
  },
  dark: {
    background: '#202020',
    backgroundSecondary: '#303030',
    backgroundTertiary: '#404040',
    overlayBackground: 'rgba(0.0, 0.0, 0.0, 0.75)',
    noteTimeBackground: '#1a237e',
    
    textPrimary: '#ffffff',
    textSecondary: '#cccccc',
    textTertiary: '#666666',
    textQuaternary: '#444444',
    textQuinary: '#383838',
    proText: '#d4af37',
    
    border: '#383838',
    borderSecondary: 'rgba(173, 184, 209, 0.3)',
    cardBorderStrong: '#555555',
    headerBorder: '#505050',
    abilityIconUsedBorder: 'rgba(0, 123, 255, 0.5)',
    abilityIconUnusedBorder: '#484848',
    timelineMarkerRoundStart: '#505050',
    
    buttonBackground: '#383838',
    buttonText: '#bbbbbb',
    buttonHover: '#4a4a4a',
    buttonActive: '#0d6efd',
    buttonPrimary: '#1d4e7d',
    buttonPrimaryHover: '#3d7ebd',
    buttonPrimaryActive: '#2d6ead',
    buttonPrimaryText: '#ffffff',
    
    headerBackground: '#252525',
    headerText: '#ffffff',
    cardBackground: '#2d2d2d',
    cardShadow: '3px 3px 3px rgba(0, 0, 0, 0.33)',
    cardShadowStrong: '0 12px 40px rgba(0, 0, 0, 0.4), 0 6px 15px rgba(0, 0, 0, 0.3)',
    controlsBoxShadow: '0px -10px 20px rgba(0, 0, 0, 0.2)',
    
    error: '#dc3545',
    success: '#198754',
    warning: '#ffc107',
    info: '#0dcaf0',

    sliderTrackBackground: '#2d2d2d',
    sliderTrackBorder: '#404040',

    // Ability usage cell colors - dark theme
    abilityCellCount1: '#1a67c2',
    abilityCellCount2: '#4a90e2',
    abilityCellCount3: '#b2d4fd',
    abilityCellUltimate1: '#b31a4d',
    abilityCellUltimate2: '#e2517d',
    abilityCellUltimate3: '#ff90b6',

    abilitiesTimelineGradientTop: '#262626',
    abilitiesTimelineGradientMiddle: '#242424',
    abilitiesTimelineGradientBottom: '#282828',

    // Map colors - dark theme (less saturated and slightly darker)
    mapAbyss: '#3b82f6',
    mapAscent: '#4ade80',
    mapBind: '#fb923c',
    mapBreeze: '#22d3ee',
    mapHaven: '#818cf8',
    mapSplit: '#f472b6',
    mapPearl: '#2dd4bf',
    mapLotus: '#c084fc',
    mapSunset: '#fb923c',
    mapIcebox: '#38bdf8',
    mapFracture: '#f87171',
    mapCorrode: '#3b82f6',

    // Map RGBA colors - dark theme
    mapAbyssRgba: '59, 130, 246',
    mapAscentRgba: '74, 222, 128',
    mapBindRgba: '251, 146, 60',
    mapBreezeRgba: '34, 211, 238',
    mapHavenRgba: '129, 140, 248',
    mapSplitRgba: '244, 114, 182',
    mapPearlRgba: '45, 212, 191',
    mapLotusRgba: '192, 132, 252',
    mapSunsetRgba: '251, 146, 60',
    mapIceboxRgba: '56, 189, 248',
    mapFractureRgba: '248, 113, 113',
    mapCorrodeRgba: '59, 130, 246',
  }
};

export function getThemeColors(theme: Theme): ThemeColors {
  return themes[theme];
}

export function setTheme(theme: Theme) {
  const colors = getThemeColors(theme);
  
  // Set CSS variables
  Object.entries(colors).forEach(([key, value]) => {
    document.documentElement.style.setProperty(`--${key}`, value);
  });

  localStorage.setItem('theme', theme);
}

export function getStoredTheme(): Theme {
  return (localStorage.getItem('theme') as Theme) || 'light';
}

export function getStoredThemePreference(): Theme {
  return (localStorage.getItem('themePreference') as Theme) || 'light';
}

export function setStoredThemePreference(theme: Theme) {
  localStorage.setItem('themePreference', theme);
}

export function applyThemeBasedOnAuth(isAuthenticated: boolean, hasSubscription: boolean) {
  const preference = getStoredThemePreference();
  
  // If authenticated but no subscription, force light theme and store preference as light
  if (isAuthenticated && !hasSubscription) {
    setStoredThemePreference('light');
    setTheme('light');
    return;
  }

  // Otherwise use stored preference if authenticated, fallback to light theme
  const theme = isAuthenticated ? preference : 'light';
  setTheme(theme);
}